import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Award, Loader2, Lock, ShieldCheck, Users } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { saveInvite } from "@/lib/invite";
import { useAuth } from "@/context/AuthContext";
import { NamtechFooter } from "@/components/poc/NamtechFooter";
import { MemberAvatar } from "@/components/red/MemberAvatar";
import { InstallGuide } from "@/components/red/InstallGuide";

const Shell = ({ children }) => (
  <div data-testid="invite-landing" className="min-h-screen max-w-md mx-auto px-5 safe-top flex flex-col">
    <div className="flex-1 py-8 space-y-5">{children}</div>
    <NamtechFooter />
  </div>
);

const Pitch = () => (
  <div className="space-y-2">
    <p className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest c-accent"><Lock className="w-3.5 h-3.5" /> Solo por invitación</p>
    <h1 className="font-heading text-4xl font-extrabold tracking-tight">CONECTA</h1>
    <ul className="space-y-1 text-sm c-text-2" data-testid="invite-pitch">
      <li>Una red privada para presentarnos contactos de confianza.</li>
      <li>Tu red solo ve el servicio y la ciudad de tus contactos, nunca sus datos.</li>
      <li>Tú decides cada presentación y registras la comisión.</li>
    </ul>
  </div>
);

const Founders = ({ f }) => (
  <div data-testid="invite-founders" className="c-card p-3 flex items-center gap-3">
    <Award className="w-6 h-6 text-[#bc9538]" />
    {f.agotados
      ? <p className="text-sm"><span className="font-semibold">Lista de espera:</span> los lugares de fundador se agotaron.</p>
      : <p className="text-sm">Fundadores: <span data-testid="invite-founders-count" className="font-bold">{f.asignados} de {f.cupo}</span></p>}
  </div>
);

export default function InviteLandingPage() {
  const { codigo } = useParams();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const inv = sp.get("inv");

  useEffect(() => {
    api.get(`/red/invitacion/${codigo}`, { params: inv ? { inv } : {} })
      .then(({ data }) => { setD(data); if (data.con_lugares) saveInvite(data.codigo); })
      .catch((e) => setError(e.response?.status === 404 ? "Esta invitación no es válida" : errMsg(e)));
  }, [codigo, inv]);

  if (error) return <Shell><Pitch /><p data-testid="invite-invalid" className="c-card p-4 text-sm font-semibold">{error}</p></Shell>;
  if (!d) return <Shell><div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div></Shell>;
  const reg = `/i/${d.codigo}/registro${inv ? `?inv=${inv}` : ""}`;
  return (
    <Shell>
      <Pitch />
      <div data-testid="invite-inviter" className="c-card p-4 flex items-center gap-3">
        <MemberAvatar m={d.invitador} />
        <div className="min-w-0">
          <p className="text-xs c-muted">Te invita</p>
          <p className="font-semibold truncate" data-testid="invite-inviter-name">{d.invitador.nombre_publico}</p>
          <p className="text-xs c-text-2">{[d.invitador.ciudad, d.invitador.es_fundador && `Fundador #${d.invitador.numero_fundador}`].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
      <Founders f={d.fundadores} />
      {!d.con_lugares ? (
        <p data-testid="invite-no-room" className="c-card p-4 text-sm font-semibold">Esta invitación ya no tiene lugares</p>
      ) : user ? (
        <Link to="/conecta" data-testid="invite-go-app" className="c-btn-primary w-full py-3.5 flex items-center justify-center gap-2"><Users className="w-4 h-4" /> Ya tienes cuenta: ir a CONECTA</Link>
      ) : (
        <div className="space-y-2.5">
          <button data-testid="invite-install-btn" onClick={() => setShowInstall(true)} className="c-btn-primary w-full py-3.5 text-base">Instalar y registrarme</button>
          <Link to="/login" data-testid="invite-login-link" className="block w-full py-3 rounded-xl c-card text-center font-semibold">Ya tengo cuenta</Link>
          {showInstall && (
            <>
              <InstallGuide />
              <Link to={reg} data-testid="invite-register-link" className="c-btn-primary w-full py-3.5 flex items-center justify-center gap-2"><ShieldCheck className="w-4 h-4" /> Registrarme ahora</Link>
            </>
          )}
        </div>
      )}
    </Shell>
  );
}
